import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { FileText, Download, Search, Image as ImageIcon } from "lucide-react";

export default function AdminDocuments({ apps, recruiters, onDownload }) {
  const [q, setQ] = useState("");

  const allDocs = useMemo(() => {
    const fromApps = (apps || []).flatMap((a) =>
      (a.documents || []).map((d) => ({
        ...d,
        owner_name: a.full_name,
        owner_kind: "Application",
        accent: "#1E3A8A",
      }))
    );
    const fromRec = (recruiters || []).flatMap((r) =>
      (r.documents || []).map((d) => ({
        ...d,
        owner_name: r.full_name,
        owner_kind: "Recruiter",
        accent: "#C49A3A",
      }))
    );
    return [...fromApps, ...fromRec].sort((a, b) =>
      (b.filename || "").localeCompare(a.filename || "")
    );
  }, [apps, recruiters]);

  const filtered = useMemo(
    () =>
      allDocs.filter((d) => {
        if (!q) return true;
        return [d.filename, d.label, d.owner_name, d.owner_kind]
          .filter(Boolean)
          .some((v) => v.toLowerCase().includes(q.toLowerCase()));
      }),
    [allDocs, q]
  );

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4 flex items-center gap-2">
        <div className="flex-1 flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-2">
          <Search className="w-4 h-4 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by filename, owner, type..."
            className="bg-transparent outline-none text-sm flex-1"
          />
        </div>
        <div className="text-xs text-slate-500 hidden sm:block">
          Total: <span className="font-semibold">{allDocs.length}</span>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-16 text-center text-slate-400 text-sm">
          No documents uploaded yet
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((d, idx) => {
            const isImg =
              d.content_type?.startsWith("image/") ||
              [".jpg", ".jpeg", ".png"].includes((d.ext || "").toLowerCase());
            return (
              <motion.div
                key={d.file_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03 }}
                whileHover={{ y: -3 }}
                className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden hover:shadow-md transition-shadow"
              >
                <div
                  className="h-28 flex items-center justify-center text-white"
                  style={{
                    background: `linear-gradient(135deg, ${d.accent} 0%, ${d.accent}A0 100%)`,
                  }}
                >
                  {isImg ? (
                    <ImageIcon className="w-10 h-10 opacity-90" />
                  ) : (
                    <FileText className="w-10 h-10 opacity-90" />
                  )}
                </div>
                <div className="p-4">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                    {d.owner_kind}
                  </div>
                  <div className="font-medium text-slate-800 truncate">
                    {d.label?.replace(/_/g, " ") || d.filename}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {d.filename}
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="text-xs text-slate-500 truncate">
                      {d.owner_name}
                    </div>
                    <button
                      onClick={() => onDownload(d.file_id, d.filename)}
                      className="inline-flex items-center gap-1 bg-cct-blue text-white text-[11px] font-semibold px-3 py-1.5 rounded-md hover:bg-[#1a3380]"
                    >
                      <Download className="w-3 h-3" /> Download
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
